import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App'; // Corregido: No se debe usar la extensión .tsx en imports
import { seedDatabase } from './db/seed';
import { initTelemetry } from './lib/telemetry';

// ─────────────────────────────────────────────────────────────
// 0. SEGURIDAD GLOBAL (Catch Early Errors)
// ─────────────────────────────────────────────────────────────
window.addEventListener('unhandledrejection', (event) => {
  console.error('🔥 NIIZ CRITICAL: Promesa no manejada detectada:', event.reason);
});

// ─────────────────────────────────────────────────────────────
// 0.5 TELEMETRÍA DE VENTAS (recolección local + envío semanal)
// ─────────────────────────────────────────────────────────────
initTelemetry();

// ─────────────────────────────────────────────────────────────
// 1. INICIALIZACIÓN DEL TEMA
// ─────────────────────────────────────────────────────────────
const applyInitialTheme = () => {
  try {
    const stored = localStorage.getItem('theme');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

    const useDark = stored === 'dark' || (stored === null && systemPrefersDark);

    if (useDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    // Limpiamos listeners anteriores si existieran (aunque en main es único)
    mediaQuery.onchange = (e) => {
      if (localStorage.getItem('theme') === null) {
        document.documentElement.classList.toggle('dark', e.matches);
      }
    };
  } catch (err) {
    console.warn('⚠️ NIIZ: Error inicializando tema:', err);
  }
};

applyInitialTheme();

// ─────────────────────────────────────────────────────────────
// 2. MONTAJE DE LA APLICACIÓN
// ─────────────────────────────────────────────────────────────
const initApp = async () => {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('❌ NIIZ: Elemento #root no encontrado en el DOM. Verifica index.html');
  }

  // Creamos el root una sola vez
  const root = createRoot(rootElement);

  console.log(
    '%c🚀 NIIZ PLATFORM%c 2025 Multi-Module %cREADY',
    'background:#0e7490;color:white;padding:10px 20px;border-radius:12px 0 0 12px;font-weight:bold;font-size:16px;',
    'background:#1e293b;color:#10b981;padding:10px 20px;font-weight:bold;font-size:16px;',
    'background:#10b981;color:white;padding:10px 20px;border-radius:0 12px 12px 0;font-weight:bold;font-size:16px;'
  );

  try {
    // Estrategia de carga: Intentamos sembrar la DB pero no bloqueamos más de 5s
    // Esto evita que la app se quede en blanco si la conexión es lenta.
    const seedPromise = seedDatabase();
    
    const timeoutPromise = new Promise<'TIMEOUT'>((resolve) =>
      setTimeout(() => resolve('TIMEOUT'), 5000)
    );

    const result = await Promise.race([seedPromise, timeoutPromise]);

    if (result === 'TIMEOUT') {
      console.warn('⏱️ NIIZ: Inicialización de datos tardó demasiado → continuando renderizado en background');
    } else {
      console.log('✅ NIIZ: Base de datos sincronizada e inicializada');
    }
  } catch (err) {
    // No detenemos la app por un error en el seed, pero lo reportamos fuerte
    console.error('⚠️ NIIZ: Error no crítico durante seedDatabase (La App iniciará igual):', err);
  } finally {
    // Renderizado final garantizado
    root.render(
      <StrictMode>
        <App />
      </StrictMode>
    );
  }
};

// Ejecución principal con captura de errores fatales
initApp().catch((err) => {
  console.error('❌ NIIZ: Error crítico al iniciar la aplicación:', err);
  // Opcional: Renderizar un fallback de error en el DOM si todo falla
  document.body.innerHTML = `<div style="color:red; padding:20px; text-align:center;"><h1>Error Crítico del Sistema</h1><p>${err.message}</p></div>`;
});